import React, { forwardRef, useEffect, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { toolbarModules, formats } from 'components/EditorToolbar';
import TextPredictionModule from 'components/EditorTextPredictionModule';
import EditorLizzyToolbarModule from 'components/EditorLizzyToolbarModule';
import { icons as lizzyIcons } from 'components/icons';
import { toolbarOptions } from 'components/ToolbarOptions';

import { setCurrentContentHtml } from 'redux/editor';
import 'react-quill/dist/quill.snow.css';

const Inline = Quill.import('blots/inline');

class TextSuggestion extends Inline {
  static create({ value, getNode }) {
    let node = super.create();
    node.setAttribute('data-ac', 'true');
    node.setAttribute('style', 'color: var(--lizzy-black-35)');

    node.textContent = ' ';

    getNode && getNode({ node });

    return node;
  }
}

TextSuggestion.blotName = 'suggestion';
TextSuggestion.className = 'suggestion';
TextSuggestion.tagName = 'span';

Quill.register(TextSuggestion);
Quill.register('modules/typingPrediction', TextPredictionModule, true);
Quill.register('modules/lizzyToolbar', EditorLizzyToolbarModule, true);

// Add a custom inline format
Quill.register('my-custom-format', {
  tagName: 'span',
  class: 'my-custom-format'
});

const modules = {
  typingPrediction: true,
  lizzyToolbar: true,
  ...toolbarModules
};

const Editor = forwardRef(({ initialValue }, ref) => {
  /* Refs */
  const quillRef = useRef(null);

  // Expose the following to the parent
  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current.getEditor()
  }));

  /* Helpers */
  const dispatch = useDispatch();

  /* Selectors */
  const currentContentHtml = useSelector(({ editor }) => editor.currentContentHtml);

  /* Effects */
  useEffect(() => {
    dispatch(setCurrentContentHtml(initialValue));
  }, [dispatch, initialValue]);

  useEffect(() => {
    // Preload icons
    [
      ...toolbarOptions.flat(),
      { icon: 'lizzy-logo-plain' },
      { icon: 'lizzy-send-chat' }
    ].forEach((option) => {
      lizzyIcons[option.icon].preload();
    });

    if (quillRef.current) {
      const quillInstance = quillRef.current.getEditor();
      const lizzyToolbar = quillInstance.getModule('lizzyToolbar');
      const typingPrediction = quillInstance.getModule('typingPrediction');

      lizzyToolbar.onMount();
      typingPrediction.onMount();

      return () => {
        lizzyToolbar.onUnmount();
        typingPrediction.onUnmount();
      };
    }
  }, []);

  /* Functions */
  const handleChange = (content, delta, source, editor) => {
    dispatch(setCurrentContentHtml(content));
  };

  return (
    <EditorWrapper data-testid='editor'>
      <ReactQuill
        ref={quillRef}
        onChange={handleChange}
        value={currentContentHtml}
        modules={modules}
        formats={formats}
        scrollingContainer='html'
      />
    </EditorWrapper>
  );
});

export default Editor;

Editor.propTypes = {
  onChange: PropTypes.func,
  onLineChange: PropTypes.func,
  textSuggestion: PropTypes.string,
  initialValue: PropTypes.string,
};

Editor.defaultProps = {
  onChange: () => {},
  onLineChange: () => {},
  initialValue: ''
}

const EditorWrapper = styled.div`
  padding: 50px 100px;

  .ql-clipboard {
    position: fixed;
  }

  .ql-editor {
    position: relative;
    z-index: 2;
    min-height: 1056px;
    padding: 0;

    &.ql-blank::before {
      left: auto;
      right: auto;
    }
  }

  .ql-container {
    border: 0;
    font-size: 18px;
    position: relative;
  }

  .selection-popup {
    position: absolute;
    padding: 16px;
    box-shadow: 1px 1px 10px -2px rgba(0,0,0,0.5);
    border-radius: 5px;
    background-color: #fff;
    z-index: 10;
  }

  .suggestion {
    position: relative;
    cursor: default;
    transition: color 0.2s ease;

    &:hover {
      /* color: var(--lizzy-black-65) !important; */
    }

    &::after {
      content: 'tab';
      border: 1px solid var(--lizzy-black-10);
      font-size: 14px;
      padding: 1px 8px;
      border-radius: 4px;
      margin: 0 4px;
    }
  }
`;
